import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import type { OutboxItem } from '@Domain/Ports/Outbox/OutboxPort';
import TRANSFER_STATUS_PRESENTATIONS from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/TransferCard/Helpers/transferStatusPresentations';
import useTransferStatus from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/TransferCard/Hooks/useTransferStatus';
import TransferCardShell from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/TransferCard/TransferCardShell';
import TransferProgress from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/TransferCard/TransferProgress';
import useOutboxPort from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxPort';
import { css } from '@emotion/react';
import { RotateCcw } from 'lucide-react';
import * as React from 'react';
import { Button } from '@verclam/ui-kit';

const failureStyle = css`
	margin: 0;
	padding: 0.75rem 1rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.ink};
`;

const retryButtonStyle = css`
	align-self: flex-start;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		align-self: stretch;
	}
`;

type Props = {
	readonly outboxItem: OutboxItem;
};

/** The transfer of the recording, read straight off the queue item of this device. */
const TransferCard: React.FC<Props> = ({ outboxItem }) => {
	const outboxPort = useOutboxPort();
	const status = useTransferStatus(outboxItem);
	const presentation = TRANSFER_STATUS_PRESENTATIONS[status];

	const [isRetrying, setIsRetrying] = React.useState(false);
	const [retryErrorMessage, setRetryErrorMessage] = React.useState<string | null>(null);

	const handleRetry = React.useCallback(() => {
		setRetryErrorMessage(null);
		setIsRetrying(true);

		outboxPort
			.retryNow(outboxItem.id)
			.catch((reason: unknown) => {
				setRetryErrorMessage(
					reason instanceof Error ? reason.message : 'La nouvelle tentative a échoué.',
				);
			})
			.finally(() => setIsRetrying(false));
	}, [outboxPort, outboxItem.id]);

	const isFailed = status === 'failed';
	const failureMessage = isFailed ? (retryErrorMessage ?? outboxItem.lastErrorMessage) : null;

	return (
		<TransferCardShell
			title={presentation.label}
			detail={presentation.detail}
			footnote={presentation.footnote}
		>
			<ConditionalLoader data={status === 'transferring'}>
				<TransferProgress progress={outboxItem.progress} segmentCount={outboxItem.segmentCount} />
			</ConditionalLoader>
			<ConditionalLoader data={failureMessage}>
				{(message) => <p css={failureStyle}>{message}</p>}
			</ConditionalLoader>
			<ConditionalLoader data={isFailed}>
				<Button
					type="button"
					severity="secondary"
					label="Réessayer le transfert"
					icon={<RotateCcw size={16} />}
					loading={isRetrying}
					onClick={handleRetry}
					css={retryButtonStyle}
				/>
			</ConditionalLoader>
		</TransferCardShell>
	);
};

export default TransferCard;
